import { useState, useEffect } from 'react';
import { apiFetchAuth } from '../api/client';
import { getMyStats } from '../client';
import { isLoggedIn, clearToken } from '../auth/auth';

// Back-end stores `created_at` using Python's `datetime.utcnow().isoformat()` (microseconds, no timezone).
// JS Date parsing is inconsistent for 6-digit fractional seconds, so normalize to ms + UTC.
const toEpoch = (value) => {
    if (!value) return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    let s = String(value).trim();
    if (!s) return 0;

    // Trim microseconds to milliseconds if present: ".123456" -> ".123"
    s = s.replace(/(\.\d{3})\d+/, '$1');

    // If timezone is missing, assume UTC (server uses utcnow()).
    const hasTz = /[zZ]$|[+-]\d{2}:\d{2}$/.test(s);
    if (!hasTz && /^\d{4}-\d{2}-\d{2}T/.test(s)) s += 'Z';

    const t = Date.parse(s);
    return Number.isFinite(t) ? t : 0;
};

export const useMyPageDashboard = (targetUserId = null) => {
    const [stats, setStats] = useState({ love: null, character: null, chugumi: null, mega: null, beyond: null });
    const [unifiedResults, setUnifiedResults] = useState([]);
    const [faceResults, setFaceResults] = useState([]);
    const [personalityResults, setPersonalityResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [loggedIn, setLoggedIn] = useState(false);
    const [entitlements, setEntitlements] = useState({});

    // Guest mode data
    const [guestData, setGuestData] = useState({
        hasPending: false,
        localStats: { love: null, character: null, chugumi: null, mega: null, beyond: null },
        localPersonality: null
    });

    // 1. Auth Sync
    useEffect(() => {
        // Migration: Ensure all guest results have IDs
        try {
            const pending = JSON.parse(localStorage.getItem('pendingResults') || '[]');
            let modified = false;
            pending.forEach((item, index) => {
                // Generate ID if missing on payload
                if (item.payload && !item.payload.id) {
                    item.payload.id = `guest_${Date.now()}_${index}`;
                    modified = true;
                }
            });
            if (modified) {
                localStorage.setItem('pendingResults', JSON.stringify(pending));
                console.log('[Dashboard] Migrated guest results with IDs');
            }
        } catch (e) {
            console.error('[Dashboard] Migration error:', e);
        }

        const syncAuth = () => {
            const ok = isLoggedIn();
            setLoggedIn(ok);
            if (!ok) {
                setEntitlements({});
                // If not logged in, load guest data
                loadGuestData();
                setLoading(false);
            }
        };

        syncAuth();
        window.addEventListener('auth-change', syncAuth);
        return () => {
            window.removeEventListener('auth-change', syncAuth);
        };
    }, []);

    const loadGuestData = () => {
        try {
            const pending = JSON.parse(localStorage.getItem('pendingResults') || '[]');
            const hasPending = pending.length > 0;

            let localStats = { love: null, character: null, chugumi: null, mega: null };
            let localPersonality = null;

            pending.forEach(item => {
                if (item.endpoint === '/me/love-test') localStats.love = item.payload;
                if (item.endpoint === '/me/character-test') localStats.character = item.payload;
                if (item.endpoint === '/me/chugumi-test') localStats.chugumi = item.payload;
                if (item.endpoint === '/me/results' && item.payload.test_type === 'mega') {
                    // Assign the whole payload which likely includes 'id' if generated by client.js
                    // But client.js adds 'id' to payload before saving.
                    // Wait, item.payload HAS the id if generated.
                    // But if it was saved BEFORE this fix, it won't have ID.
                    // We can generate one here if missing, but better if client.js did.
                    // Let's assume client.js did.
                    // But wait, the payload format for mega is: { test_type: 'mega', summary: {...}, result_label: ... }
                    // client.js modify payload to add 'id'.
                    // So item.payload.id exists.
                    // So item.payload.id exists.
                    localStats.mega = item.payload;
                }
                if (item.endpoint === '/me/results' && item.payload.test_type === 'beyond') {
                    localStats.beyond = item.payload;
                }
                if (item.endpoint === '/me/seb-test' || item.endpoint === '/me/personality-results') {
                    localPersonality = {
                        ...item.payload,
                        created_at: new Date(item.timestamp || Date.now()).toISOString()
                    };
                }
            });

            console.log("[Dashboard] Loaded Guest Data:", { hasPending, localStats: Object.keys(localStats).filter(k => !!localStats[k]), localPersonality: !!localPersonality });
            setGuestData({ hasPending, localStats, localPersonality });
            // Set stats from localStats so the UI can render them
            setStats(localStats);
        } catch (e) {
            console.error("Failed to load guest data", e);
        }
    };

    // 2. Data Fetching (Only when logged in)
    useEffect(() => {
        if (!loggedIn) return;

        console.log("[Dashboard] Fetching data for user:", targetUserId || "me");

        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                let url = '/me/results?limit=50';
                if (targetUserId) {
                    url = `/admin/users/${targetUserId}/results?limit=50`;
                }
                const [resultsData, entitlementsData] = await Promise.all([
                    apiFetchAuth(url, { cache: 'no-store' }),
                    apiFetchAuth('/payments/entitlements').catch((entitlementError) => {
                        console.warn('[Dashboard] Failed to load payment entitlements:', entitlementError);
                        return { products: {} };
                    }),
                ]);

                if (resultsData?.results) {
                    // Helper to normalize data structure
                    const normalizeUnifiedRow = (row) => {
                        // 1. Create a base object merging summary and payload
                        // IMPORTANT: Preserve original row.id and row.test_type to avoid overwrite by payload
                        const base = {
                            ...row,
                            ...(row.summary || {}),
                            ...(row.payload || {}), // Merge payload too for nested access
                            id: row.id,             // Force preserve DB ID
                            test_type: row.test_type // Force preserve test_type
                        };

                        // 2. Normalize Timestamp
                        base.created_at = base.created_at || base.timestamp || new Date().toISOString();

                        // 3. Normalize specific test types
                        if (row.test_type === 'love') {
                            // Ensure result_label exists (fallback to result_key)
                            if (!base.result_label && base.result_key) {
                                base.result_label = base.result_key;
                            }
                        }

                        if (row.test_type === 'face') {
                            // Flatten nested Face Analysis structure
                            // Check typical nested paths: payload.result, payload.resultDetail
                            const resultObj = row.payload?.result || row.payload?.resultDetail || {};

                            // Flatten key attributes if missing at top level
                            if (!base.combo_id) base.combo_id = resultObj.combo_id;
                            if (!base.features) base.features = resultObj.features;
                            if (!base.masculinity_label) base.masculinity_label = resultObj.masculinity_label;
                            if (!base.masculinity_percentile) base.masculinity_percentile = resultObj.masculinity_percentile;

                            // Also check just 'payload' itself if it was saved directly
                            if (!base.combo_id && row.payload?.combo_id) base.combo_id = row.payload.combo_id;
                        }

                        if (row.test_type === 'mega') {
                            // Unified summary is already merged, but let's ensure labels
                            if (!base.result_label && base.mbti) {
                                base.result_label = base.mbti;
                            }
                        }

                        if (row.test_type === 'chugumi') {
                            // Ensure result_key and result_label are prioritized from payload
                            if (row.payload?.result_key) base.result_key = row.payload.result_key;
                            if (row.payload?.result_label) base.result_label = row.payload.result_label;

                            if (!base.result_label && base.result_key) {
                                base.result_label = base.result_key;
                            }
                        }

                        return base;
                    };

                    const processedResults = resultsData.results
                        .map(normalizeUnifiedRow)
                        // Sort by created_at descending (newest first)
                        .sort((a, b) => toEpoch(b.created_at) - toEpoch(a.created_at));

                    console.log("[Dashboard] Processed Results Count:", processedResults.length, "Types:", [...new Set(processedResults.map(r => r.test_type))]);
                    setUnifiedResults(processedResults);

                    const faceRes = processedResults.filter(r => r.test_type === 'face');
                    const personalityRes = processedResults.filter(r => r.test_type === 'personality');
                    const chemistryRes = processedResults.filter(r => r.test_type === 'chemistry');

                    setFaceResults(faceRes);
                    setPersonalityResults(personalityRes);

                    // Derive stats from unified results
                    const love = processedResults.find(r => r.test_type === 'love');
                    const character = processedResults.find(r => r.test_type === 'character');
                    const chugumi = processedResults.find(r => r.test_type === 'chugumi');
                    const mega = processedResults.find(r => r.test_type === 'mega');
                    const beyond = processedResults.find(r => r.test_type === 'beyond');

                    // If saving to server failed, results may exist only in pendingResults (guest queue).
                    // Overlay the newest pending result so MyPage reflects the latest test immediately.
                    let pendingMega = null;
                    let pendingBeyond = null;
                    try {
                        const pending = JSON.parse(localStorage.getItem('pendingResults') || '[]');
                        pending.forEach((item) => {
                            if (item?.endpoint === '/me/results' && item?.payload?.test_type === 'mega') pendingMega = item;
                            if (item?.endpoint === '/me/results' && item?.payload?.test_type === 'beyond') pendingBeyond = item;
                        });
                    } catch (e) {
                        console.warn('[Dashboard] pendingResults parse failed', e);
                    }

                    const serverMegaTime = toEpoch(mega?.created_at);
                    const serverBeyondTime = toEpoch(beyond?.created_at);

                    const mergedMega = (pendingMega && pendingMega.timestamp > serverMegaTime) ? pendingMega.payload : mega;
                    const mergedBeyond = (pendingBeyond && pendingBeyond.timestamp > serverBeyondTime) ? pendingBeyond.payload : beyond;

                    setStats({ love, character, chugumi, mega: mergedMega, beyond: mergedBeyond });
                }
                setEntitlements(entitlementsData?.products || {});
            } catch (err) {
                console.error('[Dashboard Error]', err);
                if (err.message.includes('401') || err.message.includes('403')) {
                    console.warn('[Auth] Session expired or unauthorized. Switching to Guest Mode.');
                    clearToken();
                    setLoggedIn(false);
                    loadGuestData();
                    setError('로그인 세션이 만료되었습니다. 다시 로그인해주세요.');
                } else {
                    setError('데이터를 불러오는 중 오류가 발생했습니다: ' + err.message);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();

        // When a new test result is saved, refetch so MyPage updates immediately.
        const onResultsUpdated = () => {
            fetchData();
        };
        window.addEventListener('results-updated', onResultsUpdated);
        window.addEventListener('premium-entitlements-updated', fetchData);
        return () => {
            window.removeEventListener('results-updated', onResultsUpdated);
            window.removeEventListener('premium-entitlements-updated', fetchData);
        };
    }, [loggedIn, targetUserId]);

    return {
        loading,
        error,
        loggedIn,
        stats,
        entitlements,
        unifiedResults,
        faceResults,
        personalityResults,
        chemistryResults: unifiedResults.filter(r => r.test_type === 'chemistry'),
        guestData
    };
};
